import { type Agenda, type AgendaActivity, type EligibilityItem, type RegistrationPerson } from '@koderek/shared';
import { Alert, Anchor, Avatar, Badge, Button, Group, Modal, Paper, Stack, Text, Title } from '@mantine/core';
import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import { Link } from 'react-router';
import { IconCheck, IconMapPin } from '../../components/icons';
import { seats } from '../agenda/agendaModel';
import { formatTime } from '../editions/format';
import { rejectedReason } from '../enrollment/reasons';
import type { FamilyEnrollments } from '../enrollment/useEnrollment';
import { t } from '../../texts/pl';
import {
  dayRange,
  freeGaps,
  hourTicks,
  isBackground,
  personBlocks,
  position,
  suggestionsFor,
  type Interval,
} from './timelineModel';
import classes from './FamilyTimeline.module.css';

const f = t.familyActivities;
/** Szerokość kolumny imion i godziny na osi (px) – te same wartości w CSS (`--name-w`, `--hour-w`). */
const NAME_WIDTH = 120;
const HOUR_WIDTH = 220;
const time = (ms: number) => formatTime(new Date(ms).toISOString());

/** Bieżący czas odświeżany co minutę – linia „teraz” w dniu wydarzenia. */
function useNow(): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 60_000);
    return () => window.clearInterval(id);
  }, []);
  return now;
}

interface Props {
  slug: string;
  /** Agenda z aktualną liczbą miejsc (`withAvailability`). */
  agenda: Agenda;
  people: RegistrationPerson[];
  family: FamilyEnrollments & { items: Map<string, Map<string, EligibilityItem>> };
}

/**
 * „Plan dnia” rodziny: tor na osi godzin dla każdej osoby – zajęcia, na które jest zapisana, i wolne okna. Wolne okno
 * otwiera propozycje na ten czas: zajęcia z zapisami (zapis od razu) i wstęp wolny – wykłady, stoiska, pokazy – pasujące
 * do osoby. Rodzic zapisujący dziecko widzi od razu, na co sam może pójść w tym czasie.
 */
export function FamilyTimeline({ slug, agenda: full, people, family }: Props) {
  const now = useNow();
  const background = full.activities.filter((a) => a.status !== 'cancelled' && isBackground(a, full.types));
  const agenda = { ...full, activities: full.activities.filter((a) => !isBackground(a, full.types)) };
  const range = dayRange(agenda);
  const ticks = hourTicks(range);
  const editionYear = Number(agenda.edition.date.slice(0, 4));
  const blocking = new Set(agenda.types.filter((type) => type.blocksTime).map((type) => type.id));
  const colors = new Map(agenda.types.map((type) => [type.id, type.color]));
  const [open, setOpen] = useState<{ participantId: string; gap: Interval } | null>(null);
  const wide = `max(100%, min(100vw - var(--app-shell-navbar-offset, 0px) - 48px, ${NAME_WIDTH + (ticks.length - 1) * HOUR_WIDTH + 48}px))`;
  const showNow = now > range.start && now < range.end;

  const lanes = people.map((person) => {
    const items = family.items.get(person.participantId) ?? new Map<string, EligibilityItem>();
    const enrolled = agenda.activities.filter((a) => items.get(a.id)?.state === 'enrolled');
    const { blocks, rows } = personBlocks(enrolled);
    const busy = blocks.filter((b) => blocking.has(b.activity.typeId)).map((b) => b.interval);
    const gaps = freeGaps(busy, range).map((gap) => {
      const s = suggestionsFor(gap, person, agenda.activities, items, editionYear, now);
      return { gap, count: s.enroll.filter((a) => items.get(a.id)?.state === 'available').length + s.open.length };
    });
    return { person, items, blocks, rows, gaps };
  });

  // Zajęcia „w tle” (poza osią) – krótka informacja pod osią, kto z rodziny jest na nie zapisany.
  const backgroundEnrolled = background
    .map((a) => ({
      activity: a,
      names: people
        .filter((p) => family.items.get(p.participantId)?.get(a.id)?.state === 'enrolled')
        .map((p) => (p.isGuardian ? f.you : p.firstName)),
    }))
    .filter((row) => row.names.length);

  const openLane = open ? lanes.find((l) => l.person.participantId === open.participantId) : undefined;

  return (
    <Paper
      withBorder
      radius="lg"
      className={classes.card}
      // Jak szeroka agenda: co najmniej szerokość kolumny strony, najwyżej obszar treści obok menu – tyle, ile potrzebują
      // godziny – i wyśrodkowana względem kolumny, żeby wychodziła na boki po równo.
      style={{ width: wide, marginLeft: `calc((100% - ${wide}) / 2)` }}
    >
      <Text size="sm" c="dimmed" mb="sm">
        {f.timelineHint}
      </Text>
      <div className={classes.scroll}>
        <div className={classes.grid} style={{ '--hours': ticks.length - 1 } as CSSProperties}>
          <div className={classes.corner} />
          <div className={classes.axis} aria-hidden>
            {ticks.map((tick) => (
              <span key={tick} className={classes.tick} style={{ left: position({ start: tick, end: tick }, range).left }}>
                {time(tick)}
              </span>
            ))}
          </div>

          {lanes.map(({ person, blocks, rows, gaps }) => {
            const name = person.isGuardian ? f.you : person.firstName;
            return (
              <div key={person.participantId} className={classes.laneRow}>
                <div className={classes.name}>
                  <Avatar size={28} radius="xl" color={person.isGuardian ? 'brand' : 'orange'}>
                    {person.firstName.slice(0, 1).toUpperCase()}
                  </Avatar>
                  <Text fw={700} size="sm" truncate>
                    {name}
                  </Text>
                </div>
                <div className={classes.lane} style={{ '--rows': rows } as CSSProperties}>
                  {gaps.map(({ gap, count }) => {
                    const from = time(gap.start);
                    const to = time(gap.end);
                    return count ? (
                      <button
                        key={gap.start}
                        type="button"
                        className={classes.gap}
                        style={position(gap, range)}
                        aria-label={f.gapLabel(name, from, to, f.suggestions(count))}
                        title={f.gapLabel(name, from, to, f.suggestions(count))}
                        onClick={() => setOpen({ participantId: person.participantId, gap })}
                      >
                        <span className={classes.gapText}>+ {f.suggestions(count)}</span>
                      </button>
                    ) : (
                      <div key={gap.start} className={`${classes.gap} ${classes.gapEmpty}`} style={position(gap, range)} aria-hidden>
                        <span className={classes.gapText}>{f.free}</span>
                      </div>
                    );
                  })}
                  {blocks.map(({ activity: a, interval, row }) => {
                    const room = agenda.rooms.find((r) => r.id === a.roomId)?.name;
                    const label = f.blockLabel(name, a.title, formatTime(a.startsAt), formatTime(a.endsAt));
                    return (
                      <Link
                        key={a.id}
                        to={`/edycje/${slug}/zajecia/${encodeURIComponent(a.symbol)}`}
                        className={classes.block}
                        aria-label={label}
                        title={room ? `${label} · ${room}` : label}
                        style={
                          {
                            ...position(interval, range),
                            '--row': row,
                            '--type-color': colors.get(a.typeId) ?? 'var(--mantine-color-brand-6)',
                          } as CSSProperties
                        }
                      >
                        <span className={classes.blockTime}>
                          {formatTime(a.startsAt)}–{formatTime(a.endsAt)}
                        </span>
                        <span className={classes.blockTitle}>{a.title}</span>
                        {room ? <span className={classes.blockRoom}>{room}</span> : null}
                      </Link>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {showNow ? (
            <div className={classes.nowLayer} aria-hidden>
              <div className={classes.now} style={{ left: position({ start: now, end: now }, range).left }}>
                <span>{f.now}</span>
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {backgroundEnrolled.length ? (
        <Stack gap={2} mt="sm">
          {backgroundEnrolled.map(({ activity: a, names }) => (
            <Text key={a.id} size="sm" c="dimmed">
              {f.background(formatTime(a.startsAt), formatTime(a.endsAt), names.join(', '))}{' '}
              <Anchor component={Link} to={`/edycje/${slug}/zajecia/${encodeURIComponent(a.symbol)}`} size="sm">
                {a.title}
              </Anchor>
            </Text>
          ))}
        </Stack>
      ) : null}

      <Modal
        opened={!!openLane}
        onClose={() => setOpen(null)}
        size="lg"
        title={
          openLane && open
            ? f.gapTitle(openLane.person.isGuardian ? f.you : openLane.person.firstName, time(open.gap.start), time(open.gap.end))
            : ''
        }
      >
        {openLane && open ? (
          <GapSuggestions
            slug={slug}
            agenda={agenda}
            person={openLane.person}
            items={openLane.items}
            gap={open.gap}
            editionYear={editionYear}
            now={now}
            family={family}
          />
        ) : null}
      </Modal>
    </Paper>
  );
}

function GapSuggestions({
  slug,
  agenda,
  person,
  items,
  gap,
  editionYear,
  now,
  family,
}: {
  slug: string;
  agenda: Agenda;
  person: RegistrationPerson;
  items: Map<string, EligibilityItem>;
  gap: Interval;
  editionYear: number;
  now: number;
  family: FamilyEnrollments;
}) {
  // Liczone na bieżąco – po zapisie zajęcia zostają z „Zapisane”, a kolidujące z nimi znikają z listy.
  const { enroll, open } = suggestionsFor(gap, person, agenda.activities, items, editionYear, now);
  const rejected = rejectedReason(family.error);
  if (!enroll.length && !open.length) return <Text c="dimmed">{f.gapNothing}</Text>;
  return (
    <Stack gap="md">
      {rejected ? (
        <Alert color="orange" role="alert">
          {rejected}
        </Alert>
      ) : null}
      {enroll.length ? (
        <Stack gap={0}>
          <Title order={3} size="h5" mb={4}>
            {f.gapEnroll}
          </Title>
          {enroll.map((a) => {
            const enrolled = items.get(a.id)?.state === 'enrolled';
            const pending = family.pending?.participantId === person.participantId && family.pending.activityId === a.id;
            return (
              <SuggestionRow key={a.id} slug={slug} agenda={agenda} activity={a} extra={seats(a).label}>
                {enrolled ? (
                  <Group gap={4} wrap="nowrap" className={classes.enrolled}>
                    <IconCheck size={16} />
                    <Text size="sm" fw={600}>
                      {f.enrolled}
                    </Text>
                  </Group>
                ) : (
                  <Button size="sm" loading={pending} onClick={() => family.enroll(person.participantId, a)}>
                    {t.enrollment.enroll}
                  </Button>
                )}
              </SuggestionRow>
            );
          })}
        </Stack>
      ) : null}
      {open.length ? (
        <Stack gap={0}>
          <Title order={3} size="h5" mb={4}>
            {f.gapOpen}
          </Title>
          {open.map((a) => (
            <SuggestionRow key={a.id} slug={slug} agenda={agenda} activity={a} extra={t.agenda.freeEntry}>
              <Badge variant="light" color="gray" style={{ flex: 'none' }}>
                {agenda.types.find((type) => type.id === a.typeId)?.name}
              </Badge>
            </SuggestionRow>
          ))}
        </Stack>
      ) : null}
    </Stack>
  );
}

function SuggestionRow({
  slug,
  agenda,
  activity: a,
  extra,
  children,
}: {
  slug: string;
  agenda: Agenda;
  activity: AgendaActivity;
  extra: string;
  children: ReactNode;
}) {
  const room = agenda.rooms.find((r) => r.id === a.roomId)?.name;
  const color = agenda.types.find((type) => type.id === a.typeId)?.color;
  return (
    <Group className={classes.suggestion} justify="space-between" gap="sm" wrap="nowrap">
      <Group gap="sm" wrap="nowrap" miw={0}>
        <div className={classes.chip} style={{ '--type-color': color ?? 'var(--mantine-color-brand-6)' } as CSSProperties}>
          {formatTime(a.startsAt)}
          <br />
          {formatTime(a.endsAt)}
        </div>
        <Stack gap={2} miw={0}>
          <Anchor component={Link} to={`/edycje/${slug}/zajecia/${encodeURIComponent(a.symbol)}`} c="inherit" fw={600} lineClamp={2}>
            {a.title}
          </Anchor>
          <Group gap={8} wrap="wrap">
            {room ? (
              <Text size="xs" fw={700} c="brand.8" style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                <IconMapPin size={14} />
                {room}
              </Text>
            ) : null}
            <Text size="xs" c="dimmed">
              {extra}
            </Text>
          </Group>
        </Stack>
      </Group>
      {children}
    </Group>
  );
}
